import React, { PropsWithChildren, useContext, useEffect } from "react";
import useAuth from "../common/unhealthie/hooks/useAuth";
import useProviderDetails from "./useProviderDetails";
import { ProviderDetailsResponse } from "../fay/Api";
import * as Sentry from "@sentry/react";
import { useHotjarIdentify } from "../common/useHotjar";
import { useFayStatsig } from "../common/unhealthie/hooks/useFayStatsig";
import { setActiveUserId } from "../fay/axiosInstance";

export interface IdentityValue {
  cognitoId: string | null;
  providerId: string | null;
  type: "provider";
  details: ProviderDetailsResponse | null;
  timezone: string | null;
  isLoading: boolean;
  isProvider: boolean;
  isAdmin: boolean;
  roles: string[];
}

export const IdentityContext = React.createContext<IdentityValue>({
  cognitoId: null,
  providerId: null,
  type: "provider",
  details: null,
  timezone: null,
  isLoading: true,
  isProvider: false,
  isAdmin: false,
  roles: [],
});

const IdentityProvider = ({ children }: PropsWithChildren) => {
  const { cognitoId } = useAuth();
  const { setUserFromProviderId, setUserToNull } = useFayStatsig();

  const { providerDetails, isLoadingDetails } = useProviderDetails("me");

  useHotjarIdentify({
    userId: providerDetails?.provider_id,
    email: providerDetails?.email,
    role: "PROVIDER",
  });

  useEffect(() => {
    if (providerDetails) {
      Sentry.setUser({
        id: `provider:${providerDetails.provider_id}`,
        username: providerDetails.full_name,
      });

      setUserFromProviderId(
        providerDetails.provider_id,
        providerDetails.is_admin,
      );

      setActiveUserId("provider", providerDetails.provider_id);
    }

    return () => {
      // Assuming that when the IdentityProvider is unmounted, the user is no longer logged in.
      Sentry.setUser(null);
      setUserToNull();
    };
  }, [providerDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <IdentityContext.Provider
      value={{
        cognitoId,
        type: "provider",
        isProvider: !!providerDetails,
        providerId: providerDetails?.provider_id ?? null,
        details: providerDetails ?? null,
        timezone: providerDetails?.timezone ?? null,
        isLoading: isLoadingDetails,
        isAdmin: providerDetails?.is_admin ?? false,
        roles: providerDetails?.roles ?? [],
      }}
    >
      {children}
    </IdentityContext.Provider>
  );
};

export const useIdentity = () => {
  return useContext(IdentityContext);
};

export default IdentityProvider;
